import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { BackButton } from "@/components/common/BackButton";
import { NoBackend } from "@/components/common/NoBackend";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { MenuItem } from "@/components/design/MenuItem";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import {
  CreateDocumentDocument,
  CreateDocumentGroupDocument,
  DeleteCurriculumVitaeDocument,
  DeleteDocumentDocument,
  DeleteDocumentGroupDocument,
  DocumentContentDocument,
  MyCurriculumVitaesDocument,
  MyDocumentGroupsDocument,
  MyDocumentsDocument,
  UpdateCurriculumVitaeDocument,
  UpdateDocumentDocument,
  UpdateDocumentGroupDocument,
} from "@/graphql/generated/operations";
import { CV_API_VERSION } from "@/lib/cv-model";
import {
  graphQlErrorExtensions,
  throwForValidationResult,
  userFacingGraphqlError,
} from "@/lib/graphql-error";
import { base64ToBlob, useOpenDocument } from "@/lib/use-open-document";
import { usePendingScope } from "@/lib/pending-edits";
import { takePendingUpload } from "@/lib/pending-upload";
import { MAX_DOCUMENT_UPLOAD_BYTES } from "@/lib/upload-limits";
import { useMe } from "@/auth/use-me";
import { DocItemRow } from "./service/DocItemRow";
import { DocPreviewCard } from "./service/DocPreviewCard";
import { DocumentDeleteConflictAlert } from "./service/DocumentDeleteConflictAlert";
import { DocumentFilterBuilder } from "./service/DocumentFilterBuilder";
import { FolderRow, NewFolderRow } from "./service/FolderRow";
import { FileDropZone } from "@/components/common/FileDropZone";
import { useServiceActions } from "./service/use-service-actions";
import type {
  DocumentFilterDefinition,
  DocumentFilterKey,
  DocumentItem,
} from "./service/types";

// Save · Service - node-by-node port of workshop's screens/Save.jsx
// ServiceTab. Two-column action zone (Input / Output) feeding into a
// full-width Documents box with drag-and-drop upload + filter.
//
// Workshop's INPUT cards (Power-Analyse, CV-Extractor) are static
// design-spec previews - no backend. The OUTPUT CV-Generator opens the
// CvEditor; saving persists a re-editable CurriculumVitae draft that is
// listed alongside uploaded Documents below. PDFs are download-only
// (via the editor's previewer), mirroring discovery's CV model.
//
// Documents grid + upload uses tech-stack's existing MyDocuments +
// CreateDocument ops.

// Per-file cap, guarded client-side for a friendly error. Single source of
// truth in lib/upload-limits (mirrored in the Settings "Uploads" overview).
const MAX_UPLOAD_BYTES = MAX_DOCUMENT_UPLOAD_BYTES;

// A document/CV is flagged "new" (accent dot) for 48h after it was created -
// uploaded or generated as output.
const NEW_FILE_WINDOW_MS = 48 * 60 * 60 * 1000;

// The Documents box loads BOTH its connections (uploaded documents + CV drafts)
// in full: folder counts/contents and the "X of Y" tally must reflect every
// item, not just the first server batch. We walk the pages in PAGE_FETCH-sized
// chunks (the cursor merge in graphql/client.ts accumulates them), then show
// the fully-loaded loose/Unsorted list DOCS_PER_PAGE at a time client-side.
const PAGE_FETCH = 100;
const DOCS_PER_PAGE = 24;
const GROUPS_PER_PAGE = 18;

type ServiceError =
  | string
  | { kind: "document-used-in-shared-items"; sharedItemIds: string[] };

function documentDeleteConflict(error: unknown): ServiceError | null {
  const extensions = graphQlErrorExtensions(error, "GENESIS001218");
  const sharedItemIds = extensions?.["sharedItemIds"];
  if (!Array.isArray(sharedItemIds)) return null;

  const ids = [...new Set(sharedItemIds.filter((id): id is string => typeof id === "string"))];
  return ids.length > 0
    ? { kind: "document-used-in-shared-items", sharedItemIds: ids }
    : null;
}

function readFileAsBase64(file: File): Promise<{ mimeType: string; content: string; fileName: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      const comma = dataUrl.indexOf(",");
      resolve({
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        content: comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl,
      });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function documentFileType(fileName: string | null | undefined, category: string | null | undefined, isCv: boolean): string {
  const ext = fileName?.match(/\.([a-z0-9]{2,8})$/i)?.[1];
  if (ext) return ext.toUpperCase();
  if (category) return category.toUpperCase();
  if (isCv) return "PDF";
  return "DOC";
}

export function ServicePage() {
  const intl = useIntl();
  const {
    data,
    loading,
    error,
    refetch: docsRefetch,
    fetchMore: docsFetchMore,
  } = useQuery(MyDocumentsDocument, { variables: { input: { first: PAGE_FETCH } } });
  const docNodes = data?.documents?.nodes;
  const docs = useMemo(() => docNodes ?? [], [docNodes]);
  const {
    data: cvData,
    refetch: cvsRefetch,
    fetchMore: cvsFetchMore,
  } = useQuery(MyCurriculumVitaesDocument, { variables: { input: { first: PAGE_FETCH } } });
  const cvNodes = cvData?.curriculumVitaes?.nodes;
  const cvs = useMemo(() => cvNodes ?? [], [cvNodes]);

  // Drain every remaining page of both connections so the folder accordion and
  // the document tally see the COMPLETE set. Each appended page (merged in
  // graphql/client.ts) advances pageInfo, which re-runs the effect until the
  // connection reports no further pages.
  const docsPageInfo = data?.documents?.pageInfo;
  useEffect(() => {
    if (docsPageInfo?.hasNextPage && docsPageInfo.endCursor) {
      void docsFetchMore({
        variables: { input: { first: PAGE_FETCH, after: docsPageInfo.endCursor } },
      });
    }
  }, [docsPageInfo, docsFetchMore]);
  const cvsPageInfo = cvData?.curriculumVitaes?.pageInfo;
  useEffect(() => {
    if (cvsPageInfo?.hasNextPage && cvsPageInfo.endCursor) {
      void cvsFetchMore({
        variables: { input: { first: PAGE_FETCH, after: cvsPageInfo.endCursor } },
      });
    }
  }, [cvsPageInfo, cvsFetchMore]);
  const { data: groupsData, refetch: groupsRefetch } = useQuery(MyDocumentGroupsDocument, {
    variables: { input: {} },
  });
  // DocumentGroupOutput is a single-member union - unwrap via the any seam.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const groupsOut: any = groupsData?.documentGroups;
  const docGroups = (groupsOut?.documentGroups ?? []) as readonly { id: string; name: string }[];
  const [createDocument] = useMutation(CreateDocumentDocument);
  const [updateDocument] = useMutation(UpdateDocumentDocument);
  const [deleteDocument] = useMutation(DeleteDocumentDocument);
  const [updateCv] = useMutation(UpdateCurriculumVitaeDocument);
  const [deleteCvMutation] = useMutation(DeleteCurriculumVitaeDocument);
  const [createGroup] = useMutation(CreateDocumentGroupDocument);
  const [updateGroup] = useMutation(UpdateDocumentGroupDocument);
  const [deleteGroup] = useMutation(DeleteDocumentGroupDocument);
  const apollo = useApolloClient();
  const me = useMe();
  // Captured once (Date.now() is impure, not allowed inline in render); drives
  // the per-document "new" dot.
  const [nowMs] = useState(() => Date.now());

  const [docQuery, setDocQuery] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<ServiceError | null>(null);

  // Folder (document-group) errors show in the folder pane, next to "New folder"
  const [folderError, setFolderError] = useState<ServiceError | null>(null);
  const openDocument = useOpenDocument(setUploadError);
  // Input/Output actions (Power/Skill analysis, LinkedIn import, CV generator,
  // data export) plus their dialogs live in a shared hook, so the profile
  // ServiceMenu drives the exact same flows. See use-service-actions.
  const extractionDocs = useMemo(
    () => docs.map((d) => ({ id: d.id, name: d.name ?? "", groupId: (d.group?.id as string | null) ?? null })),
    [docs],
  );
  const { openCv, dialogs: serviceDialogs } = useServiceActions({
    docs: extractionDocs,
    groups: docGroups,
    onChanged: () => {
      void docsRefetch();
      void cvsRefetch();
    },
    onError: setUploadError,
  });
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [fileTypeFilter, setFileTypeFilter] = useState<string>("all");
  const [selectedDocFilters, setSelectedDocFilters] = useState<DocumentFilterKey[]>([]);
  // Client-side pager for the loose/Unsorted list. The full set is already
  // loaded (see the drain effects); this only governs how many rows render at
  // once. Folders are bounded buckets and show their full contents unpaged.
  const [loosePage, setLoosePage] = useState(0);
  const [groupsPage, setGroupsPage] = useState(0);
  // Accordion: folders are collapsible full-width sections (several can be
  // open at once); loose files live in the trailing "Unsortiert" section.
  const [expandedFolders, setExpandedFolders] = useState<ReadonlySet<string>>(new Set());
  const [docView, setDocView] = useState<"list" | "compact">("compact");
  const [dropTarget, setDropTarget] = useState<string | null>(null); // group id or "loose"
  const [draggingId, setDraggingId] = useState<string | null>(null); // document being dragged
  // Edit mode: read-only rows (download/open) become editable - names render
  // as visible inputs and a multi-select with bulk delete/move appears.
  // Which row is currently in inline rename mode. Only one row at a time.
  // Replaces the prior global editMode toggle — selection + rename are now
  // per-row hover/click affordances, no global "I'm editing" state.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  // Pending inline renames (id → new name). The global SaveBar (smycles-toast)
  // surfaces them and persists on "Speichern".
  const [pendingRenames, setPendingRenames] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function handleUpload(files: FileList | File[] | null, groupId: string | null = null) {
    if (!files || uploadBusy) return;
    if (!me.id) {
      setUploadError(intl.formatMessage({ id: "service.page.errNotSignedInUpload", defaultMessage: "Not signed in - upload not possible." }));
      return;
    }
    const tooBig = Array.from(files).filter((f) => f.size > MAX_UPLOAD_BYTES);
    if (tooBig.length) {
      setUploadError(
        `Zu groß (max. 10 MB pro Datei): ${tooBig.map((f) => f.name).join(", ")}.`,
      );
    }
    const uploadable = Array.from(files).filter((f) => f.size <= MAX_UPLOAD_BYTES);
    if (!uploadable.length) return;
    if (!tooBig.length) setUploadError(null);
    setUploadBusy(true);
    try {
      for (const f of uploadable) {
        const read = await readFileAsBase64(f);
        const res = await createDocument({
          variables: {
            input: {
              data: {
                name: read.fileName,
                person: { id: me.id },
                tags: [],
                // Drop target: upload straight into a folder (or loose = null).
                group: groupId ? { id: groupId } : null,
                // fileName is a top-level Document field; CreateFileDataInput
                // (content) only accepts mimeType + content.
                fileName: read.fileName,
                content: { mimeType: read.mimeType, content: read.content },
              },
              dryRun: false,
            },
          },
        });
        const out = res.data?.createDocument;
        if (out?.__typename !== "CreateDocumentResult") {
          throwForValidationResult(
            out,
            intl.formatMessage({ id: "service.page.errUploadDuplicate", defaultMessage: "Upload failed. A document with this name probably already exists." }),
          );
        }
      }
      await docsRefetch();
    } catch (e) {
      logApplicationFailure("save.service-upload");
      setUploadError(
        userFacingGraphqlError(
          e,
          intl.formatMessage({ id: "service.page.errUploadDuplicate", defaultMessage: "Upload failed. A document with this name probably already exists." }),
        ),
      );
    } finally {
      setUploadBusy(false);
    }
  }

  // Files handed off from the home "+ Datei hochladen" quick action: upload them
  // here once me.id is available (handleUpload needs the owner), then it's done.
  const consumedUploadRef = useRef(false);
  useEffect(() => {
    if (consumedUploadRef.current || !me.id) return;
    const pending = takePendingUpload();
    if (pending && pending.length) {
      consumedUploadRef.current = true;
      // Defer so the upload's setState runs after this effect, not within it.
      queueMicrotask(() => void handleUpload(pending));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.id]);

  async function handleRenameDoc(id: string, newName: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const orig = docs.find((d: any) => d.id === id);
    const tags = (orig?.tags as string[] | null) ?? [];
    setUploadError(null);
    try {
      const res = await updateDocument({
        // Re-send tags so the rename doesn't wipe the document's tags
        // (e.g. the "Lebenslauf" CV marker).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        variables: { id, input: { data: { name: newName, tags }, dryRun: false } } as any,
      });
      const out = res.data?.updateDocument;
      if (out?.__typename !== "UpdateDocumentResult") {
        throwForValidationResult(out, intl.formatMessage({ id: "service.page.errRename", defaultMessage: "Rename failed." }));
      }
      await docsRefetch();
    } catch (e) {
      setUploadError(userFacingGraphqlError(e, intl.formatMessage({ id: "service.page.errRename", defaultMessage: "Rename failed." })));
    }
  }

  async function handleDeleteDoc(id: string) {
    setUploadError(null);
    try {
      await deleteDocument({ variables: { input: { id, dryRun: false } } });
      await docsRefetch();
    } catch (e) {
      setUploadError(
        documentDeleteConflict(e) ??
          userFacingGraphqlError(e, intl.formatMessage({ id: "common.deleteFailed", defaultMessage: "Delete failed." })),
      );
    }
  }

  async function handleOpenDoc(id: string) {
    setUploadError(null);
    await openDocument(id);
  }

  // CV drafts are CurriculumVitae records, so rename/delete go through the
  // CV mutations - the document mutations don't apply to them.
  async function handleRenameCv(id: string, newName: string) {
    setUploadError(null);
    try {
      const res = await updateCv({
        variables: {
          id,
          input: { data: { apiVersion: CV_API_VERSION, name: newName }, dryRun: false },
        },
      });
      const out = res.data?.updateCurriculumVitae;
      if (out?.__typename !== "UpdateCurriculumVitaeResult") {
        throwForValidationResult(out, intl.formatMessage({ id: "service.page.errRename", defaultMessage: "Rename failed." }));
      }
      await cvsRefetch();
    } catch (e) {
      setUploadError(userFacingGraphqlError(e, intl.formatMessage({ id: "service.page.errRename", defaultMessage: "Rename failed." })));
    }
  }

  async function handleDeleteCv(id: string) {
    setUploadError(null);
    try {
      await deleteCvMutation({ variables: { input: { id, dryRun: false } } });
      await cvsRefetch();
    } catch (e) {
      setUploadError(userFacingGraphqlError(e, intl.formatMessage({ id: "common.deleteFailed", defaultMessage: "Delete failed." })));
    }
  }

  async function handleDownloadDoc(id: string) {
    setUploadError(null);
    try {
      const { data: docData } = await apollo.query({
        query: DocumentContentDocument,
        variables: { id },
        fetchPolicy: "network-only",
      });
      const doc = docData?.document;
      const file = doc?.content;
      if (!file?.content) throw new Error(intl.formatMessage({ id: "service.page.errNoContent", defaultMessage: "No content available." }));
      const blob = base64ToBlob(file.content, file.mimeType ?? "application/octet-stream");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc?.fileName ?? "dokument";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setUploadError(userFacingGraphqlError(e, intl.formatMessage({ id: "service.page.errDownload", defaultMessage: "Download failed." })));
    }
  }

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function bulkDelete() {
    for (const id of Array.from(selectedIds)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isCv = cvs.some((c: any) => c.id === id);
      if (isCv) await handleDeleteCv(id);
      else await handleDeleteDoc(id);
    }
    setSelectedIds(new Set());
  }

  async function bulkMove(groupId: string) {
    setUploadError(null);
    try {
      for (const id of Array.from(selectedIds)) {
        // CV drafts are CurriculumVitae records - they have no document
        // group, so only real documents (found in `docs`) are movable.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const orig = docs.find((d: any) => d.id === id);
        if (!orig) continue;
        const tags = (orig?.tags as string[] | null) ?? [];
        const res = await updateDocument({
          // Re-send tags (see rename) so the move doesn't wipe them.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          variables: { id, input: { data: { tags, group: { id: groupId } }, dryRun: false } } as any,
        });
        const out = res.data?.updateDocument;
        if (out?.__typename !== "UpdateDocumentResult") {
          throwForValidationResult(out, intl.formatMessage({ id: "service.page.errMove", defaultMessage: "Move failed." }));
        }
      }
      await docsRefetch();
      setSelectedIds(new Set());
    } catch (e) {
      setUploadError(userFacingGraphqlError(e, intl.formatMessage({ id: "service.page.errMove", defaultMessage: "Move failed." })));
    }
  }

  // Folder (DocumentGroup) management. Creating/renaming/deleting folders is
  // distinct from moving documents into them (bulkMove above). Deleting a
  // folder orphans its documents server-side (genesis nulls DOCUMENT_GROUP_ID),
  // so they simply reappear without a folder in the overall list.
  // Returns success — the New-folder field stays open on failure to show its error.
  async function handleCreateGroup(name: string): Promise<boolean> {
    const trimmed = name.trim();
    if (!trimmed) return false;
    setFolderError(null);
    try {
      const res = await createGroup({
        variables: { input: { person: me.id ?? undefined, data: { name: trimmed }, dryRun: false } },
      });
      const out = res.data?.createDocumentGroup;
      if (out?.__typename === "TooManyRecordsResult") {
        setFolderError(
          intl.formatMessage(
            { id: "service.page.errFolderLimit", defaultMessage: "You have reached the maximum of {limit} folders." },
            { limit: out.limit ?? "" },
          ),
        );
        return false;
      }
      if (out?.__typename !== "CreateDocumentGroupResult") {
        throwForValidationResult(out, intl.formatMessage({ id: "service.page.errFolderCreate", defaultMessage: "Folder could not be created." }));
      }
      await groupsRefetch();
      return true;
    } catch (e) {
      setFolderError(userFacingGraphqlError(e, intl.formatMessage({ id: "service.page.errFolderCreate", defaultMessage: "Folder could not be created." })));
      return false;
    }
  }

  async function handleRenameGroup(id: string, name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    setFolderError(null);
    try {
      const res = await updateGroup({
        variables: { id, input: { data: { name: trimmed }, dryRun: false } },
      });
      const out = res.data?.updateDocumentGroup;
      if (out?.__typename !== "UpdateDocumentGroupResult") {
        throwForValidationResult(out, intl.formatMessage({ id: "service.page.errFolderRename", defaultMessage: "Folder could not be renamed." }));
      }
      await groupsRefetch();
    } catch (e) {
      setFolderError(userFacingGraphqlError(e, intl.formatMessage({ id: "service.page.errFolderRename", defaultMessage: "Folder could not be renamed." })));
    }
  }

  // mode "keep": only the folder goes - genesis orphans its documents, so
  // they fall back into "Unsortiert". mode "withFiles": the contained
  // documents are deleted first, then the folder.
  async function handleDeleteGroup(id: string, mode: "keep" | "withFiles") {
    setFolderError(null);
    try {
      if (mode === "withFiles") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const inGroup = docs.filter((d: any) => (d.group?.id ?? null) === id);
        for (const d of inGroup) {
          await deleteDocument({ variables: { input: { id: d.id, dryRun: false } } });
        }
      }
      await deleteGroup({ variables: { input: { id, dryRun: false } } });
      // Refetch docs too: their group reference was orphaned server-side.
      await Promise.all([groupsRefetch(), docsRefetch()]);
      setExpandedFolders((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (e) {
      setFolderError(
        documentDeleteConflict(e) ??
          userFacingGraphqlError(e, intl.formatMessage({ id: "service.page.errFolderDelete", defaultMessage: "Folder could not be deleted." })),
      );
    }
  }

  // Move a single document into a folder (groupId) or out of any folder
  // (null). Drag & drop target; CV drafts have no group and aren't movable.
  async function moveDocToGroup(id: string, groupId: string | null) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const orig = docs.find((d: any) => d.id === id);
    if (!orig) return;
    if ((orig.group?.id ?? null) === groupId) return; // already there
    setUploadError(null);
    try {
      const tags = (orig.tags as string[] | null) ?? [];
      const res = await updateDocument({
        // Re-send tags so the move doesn't wipe them (see rename).
        variables: {
          id,
          input: { data: { tags, group: groupId ? { id: groupId } : null }, dryRun: false },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });
      const out = res.data?.updateDocument;
      if (out?.__typename !== "UpdateDocumentResult") {
        throwForValidationResult(out, intl.formatMessage({ id: "service.page.errMove", defaultMessage: "Move failed." }));
      }
      await docsRefetch();
    } catch (e) {
      setUploadError(userFacingGraphqlError(e, intl.formatMessage({ id: "service.page.errMove", defaultMessage: "Move failed." })));
    }
  }

  // Inline rename records a pending change (keyed by id); it isn't persisted
  // until the user confirms via the global SaveBar.
  const recordRename = (id: string, newName: string, originalName: string) =>
    setPendingRenames((p) => {
      const next = { ...p };
      // Keep the raw draft (even a transiently empty one) so the edit-mode
      // input stays controlled while typing; empties are skipped on save.
      if (newName !== originalName) next[id] = newName;
      else delete next[id];
      return next;
    });

  async function flushRenames() {
    for (const [id, newName] of Object.entries(pendingRenames)) {
      const trimmed = newName.trim();
      if (!trimmed) continue; // empty names are not persisted
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isCv = cvs.some((c: any) => c.id === id);
      if (isCv) await handleRenameCv(id, trimmed);
      else await handleRenameDoc(id, trimmed);
    }
    setPendingRenames({});
  }

  usePendingScope("service-documents", {
    dirty: Object.keys(pendingRenames).length > 0,
    save: flushRenames,
    discard: () => setPendingRenames({}),
  });

  const allItems = useMemo<DocumentItem[]>(() => {
    const docItems = docs.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (d: any): DocumentItem => {
        // Legacy: older generator runs stored CVs as PDF documents tagged
        // "Lebenslauf" - keep labelling those, they still open as PDF.
        const isCv = (d.tags as string[] | null)?.includes("Lebenslauf") ?? false;
        const fileName = d.fileName as string | null;
        const name = (d.name as string | null) ?? fileName ?? "-";
        const fileType = documentFileType(fileName ?? name, d.category as string | null, isCv);
        return {
          id: d.id,
          type: "doc",
          kind: isCv ? "CV" : fileType,
          name,
          when: d.createdAt as string | null,
          source: isCv
            ? intl.formatMessage({ id: "service.page.sourceCvGenerator", defaultMessage: "CV generator" })
            : intl.formatMessage({ id: "service.page.sourceUpload", defaultMessage: "File upload" }),
          fileType,
          groupId: (d.group?.id as string | null) ?? null,
          preview: d.preview?.content
            ? `data:${d.preview.mimeType ?? "image/png"};base64,${d.preview.content}`
            : null,
        };
      },
    );
    const cvItems = cvs.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (c: any): DocumentItem => ({
        id: c.id,
        type: "cv",
        kind: "CV",
        name: (c.name as string | null) ?? intl.formatMessage({ id: "service.page.cvFallbackName", defaultMessage: "CV" }),
        when: (c.createdAt as string | null) ?? null,
        source: "CV Generator",
        fileType: "CV",
        groupId: null,
        preview: null,
      }),
    );
    return [...docItems, ...cvItems].sort((a, b) =>
      String(b.when ?? "").localeCompare(String(a.when ?? "")),
    );
  }, [docs, cvs, intl]);

  // Guided tour (/tour): summary + upload, list and filter regions with live counts.
  useAssistantTour({
    label: intl.formatMessage({
      id: "service.tour.label",
      defaultMessage: "Save Documents",
    }),
    summary: intl.formatMessage(
      {
        id: "service.tour.summary",
        defaultMessage:
          "This is your documents page: {count} documents in {folders} folders, from manual uploads to generated CVs.",
      },
      { count: allItems.length, folders: docGroups.length },
    ),
    stops: [
      {
        anchor: "save.documents.upload",
        title: intl.formatMessage({ id: "service.tour.upload.title", defaultMessage: "Upload files" }),
        description: intl.formatMessage({
          id: "service.tour.upload.desc",
          defaultMessage:
            "Upload files here (max. 10 MB each), or simply drag and drop them onto a folder or the list below.",
        }),
      },
      {
        anchor: "save.documents.list",
        title: intl.formatMessage({ id: "service.tour.list.title", defaultMessage: "Your documents" }),
        description: intl.formatMessage(
          {
            id: "service.tour.list.desc",
            defaultMessage:
              "{count} documents in {folders} folders. Open a folder to see its files, drag documents between folders, and hover a row to rename, select or delete it.",
          },
          { count: allItems.length, folders: docGroups.length },
        ),
      },
      {
        anchor: "save.documents.filters",
        title: intl.formatMessage({ id: "service.tour.filters.title", defaultMessage: "Search & filter" }),
        description: intl.formatMessage({
          id: "service.tour.filters.desc",
          defaultMessage:
            "Filter your documents by name, source or file type, and switch between preview and compact view.",
        }),
      },
    ],
  });

  // Live assistant context (motive 1, ADR 0020): the user's own document storage.
  useAssistantContext([
    {
      description: "the user's documents page",
      value:
        `${allItems.length} documents in ${docGroups.length} folders` +
        (docQuery ? `; search "${docQuery}"` : "") +
        (fileTypeFilter !== "all" ? `; filtered to type ${fileTypeFilter}` : ""),
    },
  ]);

  const sourceOptions = useMemo(
    () => Array.from(new Set(allItems.map((it) => it.source))).sort((a, b) => a.localeCompare(b)),
    [allItems],
  );
  const fileTypeOptions = useMemo(
    () => Array.from(new Set(allItems.map((it) => it.fileType))).sort((a, b) => a.localeCompare(b)),
    [allItems],
  );
  const items = allItems.filter((it) => {
    const queryMatch = !docQuery.trim() || it.name.toLowerCase().includes(docQuery.trim().toLowerCase());
    const sourceMatch = sourceFilter === "all" || it.source === sourceFilter;
    const fileTypeMatch = fileTypeFilter === "all" || it.fileType === fileTypeFilter;
    return queryMatch && sourceMatch && fileTypeMatch;
  });
  const hasDocumentFilters = Boolean(docQuery.trim()) || sourceFilter !== "all" || fileTypeFilter !== "all";
  const documentFilters: readonly DocumentFilterDefinition[] = [
    {
      key: "source",
      label: intl.formatMessage({ id: "service.page.filterSource", defaultMessage: "Source" }),
      options: sourceOptions,
      value: sourceFilter,
      onChange: (next) => {
        setSourceFilter(next);
        setLoosePage(0);
      },
    },
    {
      key: "fileType",
      label: intl.formatMessage({ id: "service.page.filterFileType", defaultMessage: "Filetype" }),
      options: fileTypeOptions,
      value: fileTypeFilter,
      onChange: (next) => {
        setFileTypeFilter(next);
        setLoosePage(0);
      },
    },
  ];
  const resetDocumentFilters = () => {
    setDocQuery("");
    setSourceFilter("all");
    setFileTypeFilter("all");
    setSelectedDocFilters([]);
    setLoosePage(0);
  };
  const selectedItems = allItems.filter((it) => selectedIds.has(it.id));
  const selectionHasCv = selectedItems.some((it) => it.type === "cv");

  // Accordion partition (filtered set): each folder section shows its items
  // when expanded; loose items (no folder, incl. CV drafts) close the list.
  // Folder badge counts use the unfiltered set so a filtered search still
  // shows the real folder size. No nested folders - a folder holds only items.
  const looseItems = items.filter((it) => it.groupId === null);
  const looseTotalPages = Math.max(1, Math.ceil(looseItems.length / DOCS_PER_PAGE));
  // Clamp on read so deleting/moving items off the last page (or a filter that
  // shrinks the list) can't strand the user on a now-empty page.
  const loosePageClamped = Math.min(loosePage, looseTotalPages - 1);
  const pagedLooseItems = looseItems.slice(
    loosePageClamped * DOCS_PER_PAGE,
    loosePageClamped * DOCS_PER_PAGE + DOCS_PER_PAGE,
  );
  // Folders paginate independently of the loose list, GROUPS_PER_PAGE at a time.
  // Same clamp-on-read guard so deleting a folder off the last page can't strand
  // the user on a now-empty page.
  const groupsTotalPages = Math.max(1, Math.ceil(docGroups.length / GROUPS_PER_PAGE));
  const groupsPageClamped = Math.min(groupsPage, groupsTotalPages - 1);
  const pagedGroups = docGroups.slice(
    groupsPageClamped * GROUPS_PER_PAGE,
    groupsPageClamped * GROUPS_PER_PAGE + GROUPS_PER_PAGE,
  );
  // Prev / "Page X of Y" / Next controls, shared by the loose list and the folder
  // grid. Just the controls (no wrapper) so each call site supplies its own
  // layout; gate each call site on its own page count.
  const renderPagerControls = (
    page: number,
    totalPages: number,
    onChange: (next: number) => void,
  ) => (
    <>
      <Button
        variant="secondary"
        size="sm"
        disabled={page <= 0}
        onClick={() => onChange(page - 1)}
      >
        <FormattedMessage id="service.page.prevPage" defaultMessage="Previous" />
      </Button>
      <span className="field-label text-ink/70">
        <FormattedMessage
          id="service.page.pageOf"
          defaultMessage="Page {page} of {total}"
          values={{ page: page + 1, total: totalPages }}
        />
      </span>
      <Button
        variant="secondary"
        size="sm"
        disabled={page >= totalPages - 1}
        onClick={() => onChange(page + 1)}
      >
        <FormattedMessage id="service.page.nextPage" defaultMessage="Next" />
      </Button>
    </>
  );
  const folderItemCount = (gid: string) => allItems.filter((it) => it.groupId === gid).length;
  const toggleFolder = (gid: string) =>
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  // "list" view = discovery-style preview cards (server-rendered thumbnails);
  // "compact" view = dense one-line rows.
  const itemGridClass =
    docView === "list"
      ? // Eight preview tiles per row, each capped at the 140px "card" width
        // (Backdrop MaxHeight 140) so thumbnails stay crisp 1:1; columns shrink
        // below 140px on narrow containers instead of overflowing.
        "grid [grid-template-columns:repeat(8,minmax(0,140px))] justify-start gap-3"
      : "grid grid-cols-1 gap-2 @md:grid-cols-2 @lg:grid-cols-3";

  const renderItemRow = (it: DocumentItem) => {
    const isNew = !!it.when && nowMs - Date.parse(it.when) < NEW_FILE_WINDOW_MS;
    const shared = {
      kind: it.kind,
      name: pendingRenames[it.id] ?? it.name,
      when: it.when,
      source: it.source,
      isNew,
      isRenaming: renamingId === it.id,
      selected: selectedIds.has(it.id),
      onToggleSelect: () => toggleSelect(it.id),
      onStartRename: () => setRenamingId(it.id),
      onRename: (newName: string) => recordRename(it.id, newName, it.name),
      onStopRename: () => setRenamingId(null),
      // CV drafts reopen in the editor (so they can be edited); uploaded files
      // open as a blob in a new tab.
      onOpen: () => (it.type === "cv" ? openCv(it.id) : void handleOpenDoc(it.id)),
      onDownload: it.type === "doc" ? () => void handleDownloadDoc(it.id) : undefined,
      // Only real documents are draggable into folders. Inline rename now lives
      // inside the row's name <button> — drag is enabled regardless.
      draggable: it.type === "doc" && renamingId !== it.id,
      onDragStart: (e: React.DragEvent) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", it.id);
        setDraggingId(it.id);
      },
      onDragEnd: () => {
        setDraggingId(null);
        setDropTarget(null);
      },
    };
    return docView === "list" ? (
      <DocPreviewCard
        key={it.id}
        previewSrc={it.preview}
        cvGenerator={it.source === "CV Generator"}
        {...shared}
      />
    ) : (
      <DocItemRow key={it.id} {...shared} />
    );
  };

  const dropHandlers = (target: string, groupId: string | null) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!draggingId) return;
      e.preventDefault();
      e.stopPropagation();
      if (dropTarget !== target) setDropTarget(target);
    },
    onDragLeave: (e: React.DragEvent) => {
      // Native dragleave bubbles up from child elements, so moving between a
      // folder's inner rows/icons would clear the highlight and flicker. Only
      // clear when the pointer actually leaves this drop zone (relatedTarget
      // outside currentTarget; null = left the window entirely).
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      setDropTarget((t) => (t === target ? null : t));
    },
    onDrop: (e: React.DragEvent) => {
      // Only handle internal document drags here. File drops (no draggingId)
      // must bubble to the box's upload handler, which uploads and clears the
      // drag affordance — otherwise the blur overlay stays stuck on a folder/
      // loose-zone drop.
      if (!draggingId) return;
      e.preventDefault();
      e.stopPropagation();
      void moveDocToGroup(draggingId, groupId);
      setDropTarget(null);
      setDraggingId(null);
    },
  });

  return (
    // Generous bottom padding so the Documents box doesn't pin the working
    // area to the bottom edge of the viewport - you scroll into free space.
    <div
      className="mt-8 max-w-[1600px] space-y-5 px-4 pb-40 @sm:px-6 @lg:px-10"
      data-graphql="MyDocuments, MyCurriculumVitaes, MyDocumentGroups, CreateDocument, UpdateDocument, DeleteDocument, UpdateCurriculumVitae, DeleteCurriculumVitae, CreateDocumentGroup, UpdateDocumentGroup, DeleteDocumentGroup"
    >
      <div>
        <BackButton />
      </div>

      {/* File uploads happen per box (folder tiles + Unsorted), each wrapped
          in a FileDropZone — see ADR-0022. The container itself is no longer a
          drop target. */}
      <div className="box bg-surface p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <BoxHeader
              title={intl.formatMessage({ id: "service.page.documentsTitle", defaultMessage: "Documents" })}
              variant="hero"
            />
            <div className="field-meta leading-snug text-ink/80">
              {uploadBusy ? (
                <FormattedMessage id="service.page.uploading" defaultMessage="Uploading …" />
              ) : loading ? (
                <FormattedMessage id="common.loading" defaultMessage="Loading…" />
              ) : error ? (
                <FormattedMessage
                  id="service.page.error"
                  defaultMessage="Error: {message}"
                  values={{ message: userErrorMessage(intl, error) }}
                />
              ) : (
                <FormattedMessage
                  id="service.page.documentsHint"
                  defaultMessage="All uploaded files you provided as Input, generated as Output or uploaded manually."
                />
              )}
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-center gap-1.5" data-assistant-anchor="save.documents.upload">
            <Button
              disabled={uploadBusy}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadBusy ? (
                <FormattedMessage id="service.page.uploading" defaultMessage="Uploading …" />
              ) : (
                <FormattedMessage id="service.page.uploadFile" defaultMessage="Upload file" />
              )}
            </Button>
            <span className="text-[10px] text-ink/70">
              <FormattedMessage id="service.page.orDragAndDrop" defaultMessage="Or via drag &amp; drop" />
            </span>
            <span className="text-[10px] text-ink/55">
              <FormattedMessage id="service.page.maxPerFile" defaultMessage="max. 10 MB per file" />
            </span>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2" data-assistant-anchor="save.documents.filters">
          <div className="box-flat flex w-full max-w-xs items-center bg-surface px-3 py-1.5">
              <input
                value={docQuery}
                onChange={(e) => {
                  setDocQuery(e.target.value);
                  setLoosePage(0);
                }}
                placeholder={intl.formatMessage({
                  id: "service.page.filterPlaceholder",
                  defaultMessage: "Filter your available documents …",
                })}
                className="flex-1 bg-transparent text-[12px] placeholder:text-ink/40 focus:outline-none"
              />
            </div>

          <DocumentFilterBuilder
            filters={documentFilters}
            selected={selectedDocFilters}
            setSelected={setSelectedDocFilters}
          />

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <span className="field-label text-ink/70">
              <FormattedMessage
                id="service.page.documentsCount"
                defaultMessage="{total} documents"
                values={{ total: allItems.length }}
              />
            </span>
            {hasDocumentFilters ? (
              <button
                type="button"
                onClick={resetDocumentFilters}
                className="field-label ml-1 text-ink/70 hover:text-accent"
              >
                <FormattedMessage id="service.page.reset" defaultMessage="Reset" />
              </button>
            ) : null}
            <SegmentedControl
              value={docView}
              onChange={setDocView}
              options={[
                {
                  value: "list",
                  label: intl.formatMessage({ id: "service.page.viewPreview", defaultMessage: "Preview" }),
                },
                {
                  value: "compact",
                  label: intl.formatMessage({ id: "service.page.viewCompact", defaultMessage: "Compact" }),
                },
              ]}
            />
          </div>
        </div>

        {/* Bulk-actions bar appears the moment any item is selected. The
            checkboxes themselves are always available via row hover, so this
            bar acts as the "you have an active multi-selection" affordance. */}
        {selectedItems.length > 0 ? (
          <div className="box-flat mt-3 flex flex-wrap items-center gap-2 bg-ink/[0.06] px-3 py-2">
            <span className="field-label text-ink/80">
              <FormattedMessage
                id="service.page.selectedCount"
                defaultMessage="{count} selected"
                values={{ count: selectedItems.length }}
              />
            </span>
            {confirmBulkDelete ? (
              <>
                <span className="text-[11px] text-ink/80">
                  <FormattedMessage
                    id="service.page.confirmDelete"
                    defaultMessage="Really delete?"
                  />
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmBulkDelete(false);
                    void bulkDelete();
                  }}
                  className="rounded-full bg-red-600 px-2.5 py-0.5 text-[10px] text-white"
                >
                  <FormattedMessage id="common.delete" defaultMessage="Delete" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmBulkDelete(false)}
                  className="text-[10px] text-ink/70"
                >
                  <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                </button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={selectedItems.length === 0}
                  onClick={() => setConfirmBulkDelete(true)}
                >
                  <FormattedMessage id="common.delete" defaultMessage="Delete" />
                </Button>
                <div className="relative">
                  <Button
                    variant="secondary"
                    disabled={selectedItems.length === 0 || selectionHasCv || docGroups.length === 0}
                    title={
                      selectionHasCv
                        ? intl.formatMessage({
                            id: "service.page.cvNotMovable",
                            defaultMessage: "CV drafts cannot be moved into folders",
                          })
                        : docGroups.length === 0
                          ? intl.formatMessage({
                              id: "service.page.noFolders",
                              defaultMessage: "No folders available",
                            })
                          : undefined
                    }
                    onClick={() => setMoveOpen((o) => !o)}
                    size="sm"
                    className="gap-1"
                  >
                    <FormattedMessage
                      id="service.page.moveToFolder"
                      defaultMessage="Move to folder"
                    />
                    <svg
                      viewBox="0 0 24 24"
                      className="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </Button>
                  {moveOpen ? (
                    <>
                      <div className="fixed inset-0 z-20" onClick={() => setMoveOpen(false)} />
                      <div className="absolute left-0 top-full z-30 mt-1 w-56 overflow-auto rounded-md bg-surface py-1 shadow-lg ring-1 ring-ink/10">
                        {docGroups.map((g) => (
                          <MenuItem
                            key={g.id}
                            onClick={() => {
                              setMoveOpen(false);
                              void bulkMove(g.id);
                            }}
                          >
                            {g.name}
                          </MenuItem>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
                {selectionHasCv ? (
                  <span className="text-[10px] text-ink/70">
                    <FormattedMessage
                      id="service.page.onlyRealFilesMovable"
                      defaultMessage="Only real files can be moved into folders, not CV generator entries."
                    />
                  </span>
                ) : null}
                {selectedItems.length > 0 ? (
                  <button
                    type="button"
                    onClick={() =>
                      selectionHasCv
                        ? // Deselect only the blocking CV-Generator entries, so the
                          // remaining real files stay selected and become movable.
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            for (const it of allItems) if (it.type === "cv") next.delete(it.id);
                            return next;
                          })
                        : setSelectedIds(new Set())
                    }
                    className="field-label text-ink/70 hover:text-accent"
                  >
                    {selectionHasCv ? (
                      <FormattedMessage
                        id="service.page.removeBlockingSelection"
                        defaultMessage="Remove blocking selection"
                      />
                    ) : (
                      <FormattedMessage
                        id="service.page.clearSelection"
                        defaultMessage="Clear selection"
                      />
                    )}
                  </button>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {allItems.length === 0 && docGroups.length === 0 ? (
          <div className="mt-4" data-assistant-anchor="save.documents.list">
            <NoBackend
              label={intl.formatMessage({
                id: "service.page.noDocuments",
                defaultMessage: "No documents",
              })}
            />
          </div>
        ) : (
          // Folders as a compact 3-per-row card grid; an expanded folder stays
          // in its own column and grows DOWNWARD (items-start keeps the
          // neighbouring tiles pinned in place - no reflow to other columns).
          // Loose files trail in the "Unsortiert" section. No nesting.
          <div className="mt-4 flex flex-col gap-3" data-assistant-anchor="save.documents.list">
            <div className="grid grid-cols-1 items-start gap-2 @sm:grid-cols-2 @xl:grid-cols-3">
              {pagedGroups.map((g) => {
                const expanded = expandedFolders.has(g.id);
                const inFolder = items.filter((it) => it.groupId === g.id);
                return (
                  <FileDropZone
                    key={g.id}
                    variant="overlay"
                    className="rounded-md"
                    onFiles={(files) => void handleUpload(files, g.id)}
                    dropLabel={intl.formatMessage({
                      id: "service.page.dropIntoFolder",
                      defaultMessage: "Drop to upload into this folder",
                    })}
                  >
                  <div
                    className={
                      expanded
                        ? `overflow-hidden rounded-md bg-page/40 transition ${
                            dropTarget === g.id ? "ring-2 ring-accent" : "ring-1 ring-ink/15"
                          }`
                        : ""
                    }
                    // Expanded: the WHOLE folder box (header + contents) is the
                    // internal-move drop zone (file uploads use the FileDropZone wrapper).
                    {...(expanded ? dropHandlers(g.id, g.id) : {})}
                  >
                    <FolderRow
                      name={g.name}
                      count={folderItemCount(g.id)}
                      isOpen={expanded}
                      isDropTarget={dropTarget === g.id}
                      onOpen={() => toggleFolder(g.id)}
                      onRename={(name) => handleRenameGroup(g.id, name)}
                      onDelete={(mode) => handleDeleteGroup(g.id, mode)}
                      {...dropHandlers(g.id, g.id)}
                    />
                    {expanded ? (
                      inFolder.length === 0 ? (
                        <div className="px-3 py-3 text-[11px] text-ink/55">
                          {hasDocumentFilters ? (
                            <FormattedMessage
                              id="service.page.noMatchesInFolder"
                              defaultMessage="No matches in this folder"
                            />
                          ) : (
                            <FormattedMessage
                              id="service.page.folderEmpty"
                              defaultMessage="Folder is empty - drag documents onto the folder tile."
                            />
                          )}
                        </div>
                      ) : (
                        // Single column inside the folder's own column width.
                        <div className="grid grid-cols-1 gap-2 p-2">
                          {inFolder.map(renderItemRow)}
                        </div>
                      )
                    ) : null}
                  </div>
                  </FileDropZone>
                );
              })}
              <NewFolderRow
                onCreate={handleCreateGroup}
                error={typeof folderError === "string" ? folderError : null}
              />
              {typeof folderError !== "string" &&
              folderError?.kind === "document-used-in-shared-items" ? (
                <div className="col-span-full">
                  <DocumentDeleteConflictAlert sharedItemIds={folderError.sharedItemIds} />
                </div>
              ) : null}
            </div>
            {docGroups.length > GROUPS_PER_PAGE ? (
              <div className="flex items-center justify-center gap-3 px-2 py-2">
                {renderPagerControls(groupsPageClamped, groupsTotalPages, setGroupsPage)}
              </div>
            ) : null}

            <FileDropZone
              variant="overlay"
              className="rounded-md"
              onFiles={(files) => void handleUpload(files, null)}
            >
            <div
              className={`rounded-md border border-dashed p-1 transition ${
                dropTarget === "loose" ? "border-accent bg-ink/[0.06]" : "border-ink/10"
              }`}
              {...dropHandlers("loose", null)}
            >
              <div className="field-label px-2 py-1 text-ink/70">
                <FormattedMessage id="service.page.unsorted" defaultMessage="Unsorted" />
              </div>
              {looseItems.length === 0 ? (
                <div className="px-2 py-3 text-[11px] text-ink/55">
                  {hasDocumentFilters ? (
                    <FormattedMessage id="service.page.noMatches" defaultMessage="No matches" />
                  ) : (
                    <FormattedMessage
                      id="service.page.allInFolders"
                      defaultMessage="Everything filed in folders"
                    />
                  )}
                </div>
              ) : (
                <>
                  <div className={`p-1 ${itemGridClass}`}>{pagedLooseItems.map(renderItemRow)}</div>
                  {looseItems.length > DOCS_PER_PAGE ? (
                    <div className="flex items-center justify-center gap-3 px-2 py-2">
                      {renderPagerControls(loosePageClamped, looseTotalPages, setLoosePage)}
                    </div>
                  ) : null}
                </>
              )}
            </div>
            </FileDropZone>
          </div>
        )}

        {typeof uploadError === "string" ? (
          <div data-testid="service-error" className="mt-3 text-[11px] text-red-600">{uploadError}</div>
        ) : uploadError?.kind === "document-used-in-shared-items" ? (
          <DocumentDeleteConflictAlert sharedItemIds={uploadError.sharedItemIds} />
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            void handleUpload(e.target.files);
            e.target.value = "";
          }}
        />

      </div>

      {serviceDialogs}
    </div>
  );
}
